import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { Loader2 } from "lucide-react";
import type { CierreIvaAnual } from "@fiscalo/shared-types";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { updateCierreInputs } from "@/services/cierreService";

const MONTHS_ES = [
  "ene",
  "feb",
  "mar",
  "abr",
  "may",
  "jun",
  "jul",
  "ago",
  "set",
  "oct",
  "nov",
  "dic",
];

function monthLabel(period: string): string {
  const [y, m] = period.split("-");
  return `${MONTHS_ES[Number(m) - 1] ?? m} ${y}`;
}

function money(n: number): string {
  return n.toLocaleString("es-UY", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function parseUyAmount(raw: string): number {
  return parseFloat(raw.replace(/\./g, "").replace(",", "."));
}

/**
 * Annual IVA reconciliation for the cierre fiscal — auto-filled from the
 * tenant's invoices (débito/crédito/a-pagar/pagado per month). The only manual
 * field is the saldo a favor carried from the prior year.
 */
export function IvaAnualCard({
  tenantId,
  fiscalYear,
  ivaAnual,
  saldoAFavorEjAnteriorUyu,
}: {
  tenantId: string;
  fiscalYear: number;
  ivaAnual: CierreIvaAnual;
  saldoAFavorEjAnteriorUyu: number;
}): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [saldo, setSaldo] = useState(String(saldoAFavorEjAnteriorUyu || 0));

  useEffect(() => {
    setSaldo(String(saldoAFavorEjAnteriorUyu || 0));
  }, [saldoAFavorEjAnteriorUyu]);

  const saveSaldo = useMutation({
    mutationFn: () => {
      const value = parseUyAmount(saldo);
      return updateCierreInputs(tenantId, fiscalYear, {
        iva: {
          saldoAFavorEjAnteriorUyu:
            Number.isFinite(value) && value >= 0 ? value : 0,
        },
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["accountant", "cierre", tenantId, fiscalYear],
      });
      toast.success(t("cierre.iva.saldoSaved", "Saldo actualizado"));
    },
    onError: () => {
      toast.error(t("cierre.iva.saveError", "No pudimos guardar"));
    },
  });

  const { rows, totals, reconciliation } = ivaAnual;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">
          {t("cierre.iva.title", "IVA del ejercicio")}
        </CardTitle>
        <CardDescription>
          {t(
            "cierre.iva.description",
            "Débito y crédito mensual tomados de los comprobantes. Montos en UYU.",
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="overflow-x-auto">
          <Table className="min-w-[640px] text-sm">
            <TableHeader>
              <TableRow>
                <TableHead>{t("cierre.iva.col.month", "Mes")}</TableHead>
                <TableHead className="text-right">
                  {t("cierre.iva.col.ventas", "Ventas")}
                </TableHead>
                <TableHead className="text-right">
                  {t("cierre.iva.col.debito", "IVA ventas")}
                </TableHead>
                <TableHead className="text-right">
                  {t("cierre.iva.col.credito", "IVA compras")}
                </TableHead>
                <TableHead className="text-right">
                  {t("cierre.iva.col.aPagar", "A pagar")}
                </TableHead>
                <TableHead className="text-right">
                  {t("cierre.iva.col.pagado", "Pagado")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.period}>
                  <TableCell className="font-medium">
                    {monthLabel(r.period)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {money(r.ventasUyu)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {money(r.ivaDebitoUyu)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {money(r.ivaCreditoUyu)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {money(r.ivaAPagarUyu)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">
                    {r.ivaPagadoUyu != null ? money(r.ivaPagadoUyu) : "—"}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow className="border-t-2 font-semibold">
                <TableCell>{t("cierre.iva.total", "TOTAL")}</TableCell>
                <TableCell className="text-right tabular-nums">
                  {money(totals.ventasUyu)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {money(totals.ivaDebitoUyu)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {money(totals.ivaCreditoUyu)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {money(totals.ivaAPagarUyu)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {money(totals.ivaPagadoUyu)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>

        {/* Reconciliation (CONTROL ACUMULADO IVA) */}
        <div className="rounded-md border bg-muted/30 p-3 text-sm">
          <div className="mb-2 font-medium">
            {t("cierre.iva.recon.title", "Control acumulado de IVA")}
          </div>
          <dl className="grid gap-1">
            <ReconRow
              label={t("cierre.iva.recon.debito", "IVA ventas (débito)")}
              value={money(reconciliation.ivaDebitoTotalUyu)}
            />
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Label
                htmlFor="saldo-ej-anterior"
                className="text-muted-foreground"
              >
                {t("cierre.iva.recon.saldoEjAnterior", "Saldo a favor ej. anterior")}
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="saldo-ej-anterior"
                  inputMode="decimal"
                  className="h-8 w-32 text-right tabular-nums"
                  value={saldo}
                  onChange={(e) => setSaldo(e.target.value)}
                />
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-8"
                  onClick={() => saveSaldo.mutate()}
                  disabled={saveSaldo.isPending}
                >
                  {saveSaldo.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    t("common.save", "Guardar")
                  )}
                </Button>
              </div>
            </div>
            <ReconRow
              label={t("cierre.iva.recon.credito", "IVA compras (crédito)")}
              value={money(reconciliation.ivaCreditoTotalUyu)}
            />
            <ReconRow
              label={t("cierre.iva.recon.pagos", "Pagos del ejercicio")}
              value={money(reconciliation.pagosUyu)}
            />
            <div className="mt-1 flex items-center justify-between border-t pt-1 font-semibold">
              <span>{t("cierre.iva.recon.total", "Saldo de IVA")}</span>
              <span className="tabular-nums">
                {money(reconciliation.totalIvaUyu)}
              </span>
            </div>
          </dl>
        </div>
      </CardContent>
    </Card>
  );
}

function ReconRow({
  label,
  value,
}: {
  label: string;
  value: string;
}): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-muted-foreground">{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}
